import {
    checkUser as checkUserService,
    fetchAmplifyContext,
    fixAuthContext,
    getCognitoUser,
    getUserData,
    getUserMfaOptions,
    refreshTokens,
} from './authentication/services';
import {
    getEmail,
    isAutomatedTestingUser,
    shouldRefreshTokens,
} from './authentication/state/Auth.selectors';
import { updateAuth, updateTokens } from './amplify.config';

import { AUTH_STATES } from './authentication/constants';
import AmplifyActions from './authentication/state/amplify/Amplify.actions';
import AuthActions from './authentication/state/Auth.actions';
import LocaleActions from './common/state/locale/Locale.actions';
import UIActions from './common/state/ui/UI.actions';
import UnauthenticatedPaths from './routing/UnauthenticatedRoutes/UnauthenticatedRoutes.paths';
import { decodeJWT } from 'aws-amplify/auth';
import { determineLocale } from './common/helpers/i18n';
import { fetchLocale as fetchLocaleService } from './common/services/locale';
import { getParamsFromQueryString } from './common/helpers/URL';
import history from './common/history';
import { matchPath } from 'react-router-dom';

export const setUserIdle = isIdle => dispatch => dispatch(UIActions.setUserIdle(isIdle));
export const signOut = () => dispatch => {
    return fixAuthContext().then(session => {
        if (session && session.identityId) {
            // Sign out the user
            dispatch(AuthActions.logout());
        }
    });
};

export const fetchLocale = () => dispatch => {
    const meta = { noRefresh: true };
    const params = { locale: determineLocale() };
    dispatch(LocaleActions.fetchLocaleBegin(meta, params));

    return fetchLocaleService(params)
        .then(response => dispatch(LocaleActions.fetchLocaleSuccess(meta, params, response)))
        .catch(error => dispatch(LocaleActions.fetchLocaleFailure(meta, params, error)));
};

export const validateUser = params => async (dispatch, getState) => {
    const userData = await getUserData();
    const { phone, email } = userData;
    const missingAttributes = Object.entries({
        phone,
        email,
    })
        .filter(([_, value]) => !value)
        .map(([key]) => key);
    dispatch(AmplifyActions.getUserDataSuccess(userData));
    const { identities = {} } = userData;
    const isSso = Object.values(identities).length > 0;
    try {
        if (!isSso) {
            // Using cognito means we check for MFA options
            await getUserMfaOptions();
        }
    } catch (error) {
        // Handle MFA error
        if (!isAutomatedTestingUser(getState())) {
            // The session has expired between the last refresh and this call, or the browser was refreshed before a user was fully signed in
            await updateTokens();
            throw error;
        }
    }
    dispatch(AuthActions.confirmSignInSuccess(params, userData));
    dispatch(AuthActions.updateAuthState(AUTH_STATES.signedIn));
    if (isAutomatedTestingUser(getState())) {
        dispatch(
            AmplifyActions.nextStep({
                isSignedIn: true,
                nextStep: {
                    allowedMFATypes: [],
                    availableChallenges: [],
                    missingAttributes: missingAttributes.length > 0 ? missingAttributes : null,
                    signInStep: 'DONE',
                },
            })
        );
    }
};

export const checkUser = () => (dispatch, getState) => {
    let params = { username: getEmail(getState()) };
    dispatch(AuthActions.signInBegin(params));

    return fixAuthContext()
        .then(session => {
            shouldRefreshTokens(getState(), session) &&
                dispatch(AuthActions.refreshTokens(session.tokens));
            return session;
        })
        .then(checkUserService)
        .then(async currentUser => {
            // User is authenticated, but we need still need to check MFA setup
            await validateUser(params)(dispatch, getState);
            dispatch(AuthActions.signInSuccess(params));
            return currentUser;
        })
        .catch(async amplifyError => {
            // Check for an OAuth route SSO login. We will get here when using the 'token' grant flow
            const isOAuth = matchPath(window.location.pathname, {
                path: UnauthenticatedPaths.OAUTH,
            });
            if (isOAuth) {
                // The token grant flow sets the OAuth tokens in the hash, not the search params
                const oauthParams = getParamsFromQueryString(
                    history.location.hash.replace('#', '?')
                );
                const idToken = decodeJWT(oauthParams.id_token);
                const accessToken = decodeJWT(oauthParams.access_token);
                const {
                    ['cognito:username']: cognitoUsername,
                    email = params.username,
                    identities: [{ userId = cognitoUsername } = {}] = [],
                } = idToken.payload;
                const { username = cognitoUsername || userId } = accessToken.payload;
                // We need to set the correct username in the params, so the SSO email matches the token
                params.username = email;
                dispatch(AuthActions.refreshTokens({ idToken, accessToken }));
                dispatch(AuthActions.signInFederatedBegin(params));
                return updateTokens({ idToken, accessToken, username })
                    .then(getCognitoUser)
                    .then(currentUser => {
                        dispatch(AuthActions.signInSuccess(params));
                        dispatch(AuthActions.signInFederatedSuccess(params, currentUser));
                        dispatch(
                            AuthActions.confirmSignInSuccess(params, {
                                ...currentUser,
                                ssoEnabled: true,
                            })
                        );
                        return currentUser;
                    })
                    .catch(error => {
                        dispatch(AuthActions.signInFederatedFailure(params, error));
                        dispatch(AuthActions.signInFailure(params, error));

                        throw error;
                    });
            }
            // If we have a user saved, fetch the context
            if (params.username) {
                dispatch(AuthActions.fetchSignInContextBegin(params));

                return fetchAmplifyContext(params)
                    .catch(error => {
                        // Login disabled for customer account
                        if (error.errorCode === 'FE0009') {
                            window.location = 'https://www.osano.com/accounts/locked';
                        }

                        dispatch(AuthActions.fetchSignInContextFailure(params, error));
                        dispatch(AuthActions.signInFailure(params, error));

                        throw error;
                    })
                    .then(context => {
                        const formattedContext = updateAuth(context);
                        dispatch(AuthActions.fetchSignInContextSuccess(params, formattedContext));

                        // Check the user again
                        return refreshTokens()
                            .then(session => {
                                shouldRefreshTokens(getState(), session) &&
                                    dispatch(AuthActions.refreshTokens(session.tokens));
                                return session;
                            })
                            .then(checkUserService)
                            .then(async response => {
                                dispatch(AuthActions.signInSuccess(params, response));
                                await validateUser(params)(dispatch, getState);
                                return response;
                            })
                            .catch(error => {
                                dispatch(AuthActions.signInFailure(params, error));

                                throw error;
                            });
                    });
            }
            dispatch(AuthActions.signInFailure(params, amplifyError));

            throw amplifyError;
        });
};

export const deleteSnack = key => dispatch => {
    dispatch(UIActions.deleteSnack(key));
};
